import { apiClient } from "../../../infra/api-client"
import { BASE_URL } from "../../../global"
import type {
  EngDiagQuestionResponse,
  EngDiagSessionState,
  EngDiagSkipReason,
  EngDiagStudentAnswers,
  EngDiagSubmitResponse,
} from "../types/eng-diag.types"

/**
 * The student's side of an English diagnostics test. Everything is scoped server-side to the
 * authenticated student, so a session key from the client is never trusted on its own.
 *
 * The tutor's endpoints (assigning, previewing) are /engDiag/* and are not reachable from here.
 */
const ENDPOINTS = {
  getSessionState: "studentEngDiag/getSessionState",
  getQuestion: "studentEngDiag/getQuestion",
  submitAnswer: "studentEngDiag/submitAnswer",
  skipQuestion: "studentEngDiag/skipQuestion",
  completeTopic: "studentEngDiag/completeTopic",
  getAudio: "studentEngDiag/getAudio",
} as const

/** Either key identifies the test: the activity is what the lesson plan knows about. */
export type EngDiagSessionRef = {
  activity_key?: string
  session_key?: string
}

export async function getEngDiagSessionState(
  ref: EngDiagSessionRef,
  signal?: AbortSignal,
): Promise<EngDiagSessionState> {
  const response = await apiClient.post<EngDiagSessionState>(
    BASE_URL + ENDPOINTS.getSessionState,
    ref,
    { signal },
  )
  return response.data
}

/** The next question, or a named one. Omit the key and the server serves whatever is due. */
export async function getEngDiagQuestion(
  req: EngDiagSessionRef & { session_question_key?: string },
  signal?: AbortSignal,
): Promise<EngDiagQuestionResponse> {
  const response = await apiClient.post<EngDiagQuestionResponse>(
    BASE_URL + ENDPOINTS.getQuestion,
    req,
    { signal },
  )
  return response.data
}

/**
 * Record an answer. The reply says what comes next, never whether the answer was right - see
 * EngDiagSubmitResponse.
 */
export async function submitEngDiagAnswer(
  req: EngDiagSessionRef & {
    session_question_key: string
    response: EngDiagStudentAnswers
    duration_seconds?: number
  },
): Promise<EngDiagSubmitResponse> {
  const response = await apiClient.post<EngDiagSubmitResponse>(
    BASE_URL + ENDPOINTS.submitAnswer,
    req,
  )
  return response.data
}

/** "Skip for now". The question returns at the end of the block, up to a server-side limit. */
export async function skipEngDiagQuestion(
  req: EngDiagSessionRef & {
    session_question_key: string
    skip_reason: EngDiagSkipReason
    duration_seconds?: number
  },
): Promise<EngDiagSubmitResponse> {
  const response = await apiClient.post<EngDiagSubmitResponse>(
    BASE_URL + ENDPOINTS.skipQuestion,
    req,
  )
  return response.data
}

/**
 * Close a finished block. The server also repairs blocks left open, so this is a courtesy
 * rather than something the flow depends on.
 */
export async function completeEngDiagTopic(
  req: EngDiagSessionRef & { session_topic_key: string },
): Promise<EngDiagSubmitResponse> {
  const response = await apiClient.post<EngDiagSubmitResponse>(
    BASE_URL + ENDPOINTS.completeTopic,
    req,
  )
  return response.data
}

/**
 * The bytes of one audio clip in this student's test.
 *
 * Clips live in the database, not on a CDN, and the endpoint needs the student's token - so a
 * plain <audio src> cannot reach them. The caller turns this blob into an object URL.
 */
export async function fetchEngDiagAudio(
  audioId: string,
  signal?: AbortSignal,
): Promise<Blob> {
  const response = await apiClient.post(
    BASE_URL + ENDPOINTS.getAudio,
    { audio_id: audioId },
    { responseType: "blob", signal },
  )
  return response.data as Blob
}
